import React, { useState } from 'react';
import { XIcon, CheckIcon, ChevronRightIcon, ChevronLeftIcon, CalendarIcon, MapPinIcon } from 'lucide-react';
interface CreateCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const STEPS = [{
  id: 1,
  title: 'Campaign Details'
}, {
  id: 2,
  title: 'Target Partners'
}, {
  id: 3,
  title: 'Budget & Duration'
}, {
  id: 4,
  title: 'Review'
}];
export function CreateCampaignModal({
  isOpen,
  onClose
}: CreateCampaignModalProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    objective: 'Lead Generation',
    targetType: 'All Partners',
    budget: '',
    startDate: '',
    endDate: ''
  });
  if (!isOpen) return null;
  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    } else {
      // Submit logic here
      onClose();
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  return <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose} data-id="element-437">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()} data-id="element-438">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-439">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-440">
            Create New Campaign
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500" data-id="element-441">
            <XIcon size={20} data-id="element-442" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-8 py-6" data-id="element-443">
          <div className="flex items-center justify-between relative" data-id="element-444">
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-full h-0.5 bg-gray-200 -z-10" data-id="element-445"></div>
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 h-0.5 bg-blue-600 -z-10 transition-all duration-300" style={{
            width: `${(currentStep - 1) / (STEPS.length - 1) * 100}%`
          }} data-id="element-446"></div>

            {STEPS.map(step => {
            const isCompleted = step.id < currentStep;
            const isActive = step.id === currentStep;
            return <div key={step.id} className="flex flex-col items-center bg-white px-2" data-id="element-447">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors duration-300 ${isCompleted ? 'bg-green-500 text-white' : isActive ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-448">
                    {isCompleted ? <CheckIcon size={16} data-id="element-449" /> : step.id}
                  </div>
                  <span className={`text-xs mt-2 font-medium ${isActive ? 'text-blue-600' : 'text-gray-500'}`} data-id="element-450">
                    {step.title}
                  </span>
                </div>;
          })}
          </div>
        </div>

        {/* Content */}
        <div className="px-8 py-4 min-h-[320px]" data-id="element-451">
          {currentStep === 1 && <div className="space-y-4 animate-in slide-in-from-right-4 duration-300" data-id="element-452">
              <div data-id="element-453">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-454">
                  Campaign Name
                </label>
                <input type="text" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="e.g. Diwali Festive Offer 2024" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} data-id="element-455" />
              </div>
              <div data-id="element-456">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-457">
                  Objective
                </label>
                <select className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer bg-white" value={formData.objective} onChange={e => setFormData({
              ...formData,
              objective: e.target.value
            })} data-id="element-458">
                  <option data-id="element-459">Lead Generation</option>
                  <option data-id="element-460">Brand Awareness</option>
                  <option data-id="element-461">Sales Conversion</option>
                  <option data-id="element-462">Event Promotion</option>
                </select>
              </div>
              <div data-id="element-463">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-464">
                  Description
                </label>
                <textarea className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none h-24" placeholder="Describe the campaign goals and details..." value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} data-id="element-465" />
              </div>
            </div>}

          {currentStep === 2 && <div className="space-y-6 animate-in slide-in-from-right-4 duration-300" data-id="element-466">
              <div data-id="element-467">
                <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-468">
                  Target Audience
                </label>
                <div className="grid grid-cols-2 gap-4" data-id="element-469">
                  <label className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${formData.targetType === 'All Partners' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-470">
                    <input type="radio" name="targetType" className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" checked={formData.targetType === 'All Partners'} onChange={() => setFormData({
                  ...formData,
                  targetType: 'All Partners'
                })} data-id="element-471" />
                    <div className="ml-3" data-id="element-472">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-473">
                        All Partners
                      </span>
                      <span className="block text-xs text-gray-500" data-id="element-474">
                        Deploy to entire network
                      </span>
                    </div>
                  </label>
                  <label className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${formData.targetType === 'Specific Tiers' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-475">
                    <input type="radio" name="targetType" className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" checked={formData.targetType === 'Specific Tiers'} onChange={() => setFormData({
                  ...formData,
                  targetType: 'Specific Tiers'
                })} data-id="element-476" />
                    <div className="ml-3" data-id="element-477">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-478">
                        Specific Tiers
                      </span>
                      <span className="block text-xs text-gray-500" data-id="element-479">
                        Select partner tiers
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              {formData.targetType === 'Specific Tiers' && <div className="space-y-3" data-id="element-480">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-481">
                    Select Tiers
                  </label>
                  <div className="flex gap-3" data-id="element-482">
                    {['Platinum', 'Gold', 'Silver', 'Bronze'].map(tier => <label key={tier} className="flex items-center px-4 py-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-483">
                        <input type="checkbox" className="w-4 h-4 text-blue-600 rounded border-gray-300" data-id="element-484" />
                        <span className="ml-2 text-sm text-gray-700" data-id="element-485">
                          {tier}
                        </span>
                      </label>)}
                  </div>
                </div>}

              <div data-id="element-486">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-487">
                  Region Filter (Optional)
                </label>
                <div className="relative" data-id="element-488">
                  <MapPinIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-489" />
                  <select className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white cursor-pointer" data-id="element-490">
                    <option data-id="element-491">All Regions</option>
                    <option data-id="element-492">North India</option>
                    <option data-id="element-493">South India</option>
                    <option data-id="element-494">East India</option>
                    <option data-id="element-495">West India</option>
                  </select>
                </div>
              </div>
            </div>}

          {currentStep === 3 && <div className="space-y-6 animate-in slide-in-from-right-4 duration-300" data-id="element-496">
              <div data-id="element-497">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-498">
                  Total Budget Allocation
                </label>
                <div className="relative" data-id="element-499">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium" data-id="element-500">
                    ₹
                  </span>
                  <input type="number" className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="50,000" value={formData.budget} onChange={e => setFormData({
                ...formData,
                budget: e.target.value
              })} data-id="element-501" />
                </div>
                <p className="text-xs text-gray-500 mt-1" data-id="element-502">
                  This budget will be distributed among selected partners.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-503">
                <div data-id="element-504">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-505">
                    Start Date
                  </label>
                  <div className="relative" data-id="element-506">
                    <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-507" />
                    <input type="date" className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={formData.startDate} onChange={e => setFormData({
                  ...formData,
                  startDate: e.target.value
                })} data-id="element-508" />
                  </div>
                </div>
                <div data-id="element-509">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-510">
                    End Date
                  </label>
                  <div className="relative" data-id="element-511">
                    <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-512" />
                    <input type="date" className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={formData.endDate} onChange={e => setFormData({
                  ...formData,
                  endDate: e.target.value
                })} data-id="element-513" />
                  </div>
                </div>
              </div>
            </div>}

          {currentStep === 4 && <div className="space-y-6 animate-in slide-in-from-right-4 duration-300" data-id="element-514">
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 space-y-4" data-id="element-515">
                <div className="flex justify-between border-b border-gray-200 pb-3" data-id="element-516">
                  <span className="text-sm text-gray-500" data-id="element-517">Campaign Name</span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-518">
                    {formData.name || 'Untitled Campaign'}
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-200 pb-3" data-id="element-519">
                  <span className="text-sm text-gray-500" data-id="element-520">Objective</span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-521">
                    {formData.objective}
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-200 pb-3" data-id="element-522">
                  <span className="text-sm text-gray-500" data-id="element-523">Target Audience</span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-524">
                    {formData.targetType}
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-200 pb-3" data-id="element-525">
                  <span className="text-sm text-gray-500" data-id="element-526">Budget</span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-527">
                    ₹{formData.budget || '0'}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-528">
                  <span className="text-sm text-gray-500" data-id="element-529">Duration</span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-530">
                    {formData.startDate} to {formData.endDate}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-100" data-id="element-531">
                <div className="p-1 bg-blue-100 rounded-full text-blue-600 mt-0.5" data-id="element-532">
                  <CheckIcon size={14} data-id="element-533" />
                </div>
                <div data-id="element-534">
                  <h4 className="text-sm font-medium text-blue-900" data-id="element-535">
                    Ready to Launch
                  </h4>
                  <p className="text-xs text-blue-700 mt-1" data-id="element-536">
                    This campaign will be distributed to 42 partners. They will
                    receive a notification to accept and activate it.
                  </p>
                </div>
              </div>
            </div>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center" data-id="element-537">
          <button onClick={handleBack} className={`flex items-center px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors ${currentStep === 1 ? 'invisible' : ''}`} data-id="element-538">
            <ChevronLeftIcon size={16} className="mr-1" data-id="element-539" />
            Back
          </button>
          <div className="flex gap-3" data-id="element-540">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded-lg transition-colors" data-id="element-541">
              Cancel
            </button>
            <button onClick={handleNext} className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 shadow-sm transition-all transform active:scale-95" data-id="element-542">
              {currentStep === STEPS.length ? 'Launch Campaign' : 'Next Step'}
              {currentStep !== STEPS.length && <ChevronRightIcon size={16} className="ml-1" data-id="element-543" />}
            </button>
          </div>
        </div>
      </div>
    </div>;
}